import type { NarrativeValuation } from '@/pages/Narrative/hooks/useNarrativeById';
import type {
  PastAnnualRevenueEarnings,
  PastTimeSeriesForCalculations,
  YearlyTimeseries,
  YearlyTimeseriesForCalculations,
} from '@/pages/Narrative/types';
import { format } from 'date-fns';

export const mapToTimeSeriesForCalculations = (
  data: NarrativeValuation['fiscalData']
): PastTimeSeriesForCalculations => {
  const mostRecentReportedDate = data?.mostRecentReportedDate;
  const yearlyTimeSeries = data?.yearlyTimeSeries;

  if (
    !yearlyTimeSeries ||
    (Array.isArray(yearlyTimeSeries) && yearlyTimeSeries.length < 1) ||
    !mostRecentReportedDate
  ) {
    return {
      mostRecentReportedDate: '',
      yearlyTimeSeries: {},
    };
  }

  return {
    mostRecentReportedDate,
    yearlyTimeSeries: yearlyTimeSeries.reduce<YearlyTimeseriesForCalculations>(
      (acc, { year, data }) => {
        acc[year] = {
          netIncome: data.netIncome,
          revenue: data.revenue,
          totalSharesOutstanding: data.totalSharesOutstanding ?? 0,
        };
        return acc;
      },
      {}
    ),
  };
};

export const mapToAnnualRevenueEarnings = (
  data: NarrativeValuation['fiscalData']
): PastAnnualRevenueEarnings => {
  const mostRecentReportedDate = data?.mostRecentReportedDate;
  const yearlyTimeSeries = data?.yearlyTimeSeries;

  if (
    !yearlyTimeSeries ||
    (Array.isArray(yearlyTimeSeries) && yearlyTimeSeries.length < 1) ||
    !mostRecentReportedDate
  ) {
    return {
      mostRecentReportedDate: '',
      yearlyTimeSeries: {},
    };
  }

  return {
    mostRecentReportedDate,
    yearlyTimeSeries: yearlyTimeSeries.reduce<YearlyTimeseries>(
      (acc, { year, data }) => {
        acc[year] = {
          netIncome: data.netIncome,
          revenue: data.revenue,
        };
        return acc;
      },
      {}
    ),
  };
};

type CompanyAnalysisPast = {
  revenueLtmHistory: Record<string, unknown> | unknown[] | null;
  netIncomeLtmHistory: Record<string, unknown> | unknown[] | null;
  revenue: number | null;
  netIncomeMargin: number | null;
  netIncome: number | null;
} | null;

type LtmHistory = {
  [key: number]: number;
};

export const mapFromCompanyToAnnualRevenueEarnings = (
  companyAnalysisPast: CompanyAnalysisPast,
  reportingUnit: number,
  today = new Date()
) => {
  const mostRecentReportedDate = format(today, 'yyyy-MM-dd');

  const revenueLtmHistory = (companyAnalysisPast?.revenueLtmHistory ||
    {}) as LtmHistory;
  const netIncomeLtmHistory = (companyAnalysisPast?.netIncomeLtmHistory ||
    {}) as LtmHistory;

  const years = Math.max(
    Object.keys(revenueLtmHistory).length,
    Object.keys(netIncomeLtmHistory).length
  );

  const fullYear = today.getFullYear();
  const yearlyTimeSeries = {};

  for (let i = 0; i < years; i++) {
    const year = fullYear - i;
    yearlyTimeSeries[year] = {
      revenue: (revenueLtmHistory[-i] || 0) * reportingUnit,
      netIncome: (netIncomeLtmHistory[-i] || 0) * reportingUnit,
    };
  }

  return {
    mostRecentReportedDate,
    yearlyTimeSeries,
  };
};
